import React, { useState } from 'react'
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  makeStyles,
  useTheme
} from '@material-ui/core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

const useStyles = makeStyles((theme) => ({
  viewMore: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '1px 8px',
    borderRadius: 10,
    fontSize: 11,
    fontWeight: 700,
    cursor: 'pointer',
    marginLeft: 6,
    border: `1px solid ${theme.palette.primary.main}`,
    color: theme.palette.primary.main,
    backgroundColor: theme.palette.primary.main + '14',
    whiteSpace: 'nowrap',
    '&:hover': { backgroundColor: theme.palette.primary.main + '28' }
  }
}))

/**
 * RemarkCell — truncated remark text with "View more" badge.
 *
 * Shows first `maxLength` chars inline.
 * If text exceeds limit, renders a "View more" badge that opens a dialog
 * with the full remark text.
 *
 * Props:
 *   value      {string}  - the remark text to display
 *   maxLength  {number}  - chars before truncating (default: 60)
 *
 * Usage:
 *   import RemarkCell from '../Common/components/RemarkCell'
 *
 *   <RemarkCell value={log.remarks} />
 *   <RemarkCell value={log.remarks} maxLength={80} />
 */
function RemarkCell({ value, maxLength = 60 }) {
  const classes = useStyles()
  const theme = useTheme()
  const [open, setOpen] = useState(false)

  if (!value) return <span style={{ color: theme.palette.text.disabled }}>—</span>

  const isLong = value.length > maxLength
  const preview = isLong ? value.slice(0, maxLength) + '…' : value

  return (
    <>
      <Box display='flex' alignItems='center' flexWrap='wrap' style={{ gap: 4 }}>
        <Typography variant='caption'>{preview}</Typography>
        {isLong && (
          <span className={classes.viewMore} onClick={() => setOpen(true)}>
            View more
          </span>
        )}
      </Box>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth='sm' fullWidth>
        <DialogTitle>
          <Box display='flex' alignItems='center' justifyContent='space-between'>
            <Typography variant='subtitle1' style={{ fontWeight: 600 }}>Audit Remarks</Typography>
            <IconButton size='small' onClick={() => setOpen(false)}>
              <FontAwesomeIcon icon={['fal', 'xmark']} style={{ fontSize: 14 }} />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant='body2' style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {value}
          </Typography>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default RemarkCell
